import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, Link } from "../navigation";
import clsx from "clsx";
import _ from 'lodash'

import { CostItem, useCosts } from "./CostsProvider";
import { memberUrl } from "../patents/utils";
import { IconEdit } from "../components/icons";
import DeleteButton from "../components/DeleteButton";
import { family_member, trade_mark, design_registration, competitor_member } from "../data";
import { useExternal } from "../external";
import { competitorMemberUrl } from "../external/utils";
import { SortButton } from "../patents/datawizard/DataWizard";
import { useLocalState } from "../settings/localStorage";
import { usePatents } from "../patents/PatentsProvider";
import { useRoles } from "../user/Auth";
import { useFxContext } from "../forecast/FxProvider";
import { useSingleTeamMangement } from "../Management";
import { useTrademarks } from "../trademarks/TrademarksProvider";
import { trademarkUrl } from "../trademarks/utils";
import { useDesigns } from "../designs/DesignsProvider";
import { designRegistrationUrl } from "../designs/utils";


// PATENTS
export function FamilyCosts({patentFamilyId}) {
    const {membersByFamilyId} = usePatents()

    const members = membersByFamilyId[patentFamilyId] ?? []

    return <PatentCosts familyMemberIds={members.map(m => m.familyMemberId)} />
}

export function MemberCosts({familyMemberId}) {
    return <PatentCosts familyMemberIds={[familyMemberId]} />
}

function PatentCosts({familyMemberIds}: {familyMemberIds: number[]}) {
    const {memberById} = usePatents()

    return <CostsTable {...{
        entityIds: familyMemberIds,
        entity: family_member,
        getUrl: (id: number) => memberUrl(memberById[id]),
        getReference: (id: number) => memberById[id]?.internalReference
    }} />
}

// TRADEMARKS
export function TmFamilyCosts({familyId}: {familyId: number}) {
    const {trademarksByFamilyId} = useTrademarks()

    const trademarks = trademarksByFamilyId[familyId] ?? []

    return <TrademarkCosts trademarkIds={trademarks.map(t => t.trademarkId)} />
}

export function TmMemberCosts({trademarkId}: {trademarkId: number}) {
    return <TrademarkCosts trademarkIds={[trademarkId]} />
}

function TrademarkCosts({trademarkIds}: {trademarkIds: number[]}) {
    const {trademarkById} = useTrademarks()

    return <CostsTable {...{
        entityIds: trademarkIds,
        entity: trade_mark,
        getUrl: (id: number) => trademarkUrl(trademarkById[id]),
        getReference: (id: number) => trademarkById[id]?.reference
    }} />
}

// DESIGNS
export function DesignFamilyCosts({designFamilyId}: {designFamilyId: number}) {
    const {registrationsByFamilyId} = useDesigns()
    const registrations = registrationsByFamilyId[designFamilyId] ?? []
    return <DesignCosts registrationIds={registrations.map(r => r.registrationId)} />
}

export function DesignRegistrationCosts({registrationId}: {registrationId: number}) {
    return <DesignCosts registrationIds={[registrationId]} />
}

// EXTERNAL / COMPETITOR
export function ExternalFamilyCosts({competitorFamilyId}: {competitorFamilyId: number}) {
    const {membersByFamilyId, memberById} = useExternal()

    const members = membersByFamilyId[competitorFamilyId] ?? []

    return <CostsTable {...{
        entityIds: members.map(m => m.competitorMemberId),
        entity: competitor_member,
        getUrl: (id: number) => competitorMemberUrl(memberById[id]),
        getReference: (id: number) => memberById[id]?.internalReference
    }} />
}

export function ExternalMemberCosts({competitorMemberId}: {competitorMemberId: number}) {
    const {memberById} = useExternal()

    return <CostsTable {...{
        entityIds: [competitorMemberId],
        entity: competitor_member,
        getUrl: (id: number) => competitorMemberUrl(memberById[id]),
        getReference: (id: number) => memberById[id]?.internalReference
    }} />
}

function DesignCosts({registrationIds}: {registrationIds: number[]}) {
    const {registrationById} = useDesigns()

    return <CostsTable {...{
        entityIds: registrationIds,
        entity: design_registration,
        getUrl: (id: number) => designRegistrationUrl(registrationById[id]),
        getReference: (id: number) => registrationById[id]?.reference
    }} />
}


// BASIC TABLE
const format = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

type CostRow = CostItem & { entityId?: number }

/** Presentational cost table using the design-system `pc-*` classes. Reused by the per-entity
 *  cost views and the IP-management costs page. Sorting is owned by the caller — this only
 *  renders rows and emits sort changes. Pass `referenceColumn` to show the linked-entity column
 *  (omit it for the fixed-entity management view). */
export function PureCostsTable({
    costs, sortField, sortOrder, setSortField, setSortOrder, referenceColumn, renderActions, className = "tabular-nums",
}: {
    costs: CostRow[],
    sortField: string, sortOrder: number,
    setSortField: (field: string) => void, setSortOrder: (order: number) => void,
    referenceColumn?: { header: string, getUrl: (id: number) => string, getReference: (id: number) => string },
    renderActions: (cost: CostRow) => ReactNode,
    className?: string,
}) {
    const { t } = useTranslation()

    const columns: { label: string, field: string, right?: boolean }[] = [
        { label: t('date'), field: 'date' },
        ...(referenceColumn ? [{ label: referenceColumn.header, field: 'member.internalReference' }] : []),
        { label: t('amount-excl-vat'), field: 'amount', right: true },
        { label: t('vat'), field: 'vat', right: true },
        { label: t('invoice-number'), field: 'reference' },
        { label: t('cost-center'), field: 'costCenter' },
        { label: t('comment'), field: 'comment' },
    ]

    return (
        <table className={className}>
            <thead>
                <tr className="pc-header-row">
                    {columns.map(({ label, field, right }) =>
                        <th key={field} className={clsx("pc-header-cell", right && "text-right")}>
                            {label} <SortButton searchField={field} {...{ sortField, sortOrder, setSortField, setSortOrder }} />
                        </th>)}
                    <th className="pc-header-cell" />
                </tr>
            </thead>
            <tbody>
                {costs.length === 0 &&
                    <tr><td className="pc-cell text-slate-600" colSpan={columns.length + 1}>{t('no-costs-yet')} {t('click-top-right-to-start')}</td></tr>}
                {costs.map(c =>
                    <tr key={c.costId}>
                        <td className="pc-cell whitespace-nowrap align-top">{c.date}</td>
                        {referenceColumn &&
                            <td className="pc-cell whitespace-nowrap align-top">
                                <Link className="text-pcx-600 hover:text-pcx-800 font-medium" to={referenceColumn.getUrl(c.entityId!)}>{referenceColumn.getReference(c.entityId!)}</Link>
                            </td>}
                        <td className="pc-cell text-right whitespace-nowrap align-top">{format.format(c.amount)} {c.currency}</td>
                        <td className="pc-cell text-right whitespace-nowrap align-top">{format.format(c.vat)} {c.currency}</td>
                        <td className="pc-cell whitespace-nowrap align-top">{c.reference}</td>
                        <td className="pc-cell whitespace-nowrap align-top">{c.costCenter ?? '-'}</td>
                        <td className="pc-cell text-slate-600 max-w-xs truncate">{c.comment}</td>
                        <td className="flex flex-row gap-1 pc-cell">{renderActions(c)}</td>
                    </tr>)}
            </tbody>
            <TotalCostsFooter costs={costs} hasReferenceColumn={!!referenceColumn} />
        </table>
    )
}

function CostsTable(
    {entityIds, entity, getReference, getUrl}:
    {entityIds: number[], entity: string, getReference: (id: number) => string, getUrl: (id: number) => string}) {
    const {t} = useTranslation()
    const {isEditUser} = useRoles()

    const [sortField, setSortField] = useLocalState('family-costs-sort-field', 'date')
    const [sortOrder, setSortOrder] = useLocalState('family-costs-sort-order', 1)

    const {costsByEntity, deleteCost} = useCosts()
    const costsByEntityId = costsByEntity[entity] ?? {}

    const costs: CostRow[] = _(entityIds)
        .flatMap((entityId: number) => (costsByEntityId[entityId] ?? []).map(c => ({...c, entityId})))
        .sortBy(c => c[sortField])
        .thru(cs => sortOrder === 1 ? cs : cs.reverse())
        .value()

    const baseSearch = {entity, limitedIds: entityIds.join(",")}

    function Actions(c: CostRow) {
        return <>
            <Link to={{ pathname: 'cost', search: `?${createSearchParams({ ...baseSearch, costId: '' + c.costId })}` }} className="btn-secondary w-6 h-6 p-0.5">
                <IconEdit />
            </Link>
            <DeleteButton
                className="w-6 h-6 p-0.5 btn-warn-secondary"
                deleteAction={() => deleteCost(c)}
                question={t('really-delete-name', {name: `${c.reference} (${getReference(c.entityId!)})`})}
             />
        </>
    }

    return (
        <div>
            <div className="flex flex-row gap-2 justify-between">
                <h3 id="costs" className="sm:pl-2">{t('costs')}</h3>
                {isEditUser && <Link to={{ pathname: 'cost', search: `?${createSearchParams(baseSearch)}` }} className="btn-secondary text-sm">
                    {t('add-cost')}
                </Link>}
            </div>
            <div className="lg:hidden pt-4">
                {costs.map(cost =>
                    <div key={cost.costId} className="space-y-2 p-2 border-t-2 last:border-b-2 border-pcx-300">
                        <div className="flex flex-row gap-1">
                            <Link
                                className="text-pcx-600 hover:text-pcx-800 font-medium text-lg mr-auto"
                                to={getUrl(cost.entityId!)}>{getReference(cost.entityId!)}</Link>
                            <Actions {...cost} />
                        </div>
                        <CostItemDetails {...{cost, format}} />
                    </div>
                )}
            </div>
            <PureCostsTable
                className="hidden lg:table tabular-nums"
                costs={costs}
                {...{ sortField, sortOrder, setSortField, setSortOrder }}
                referenceColumn={{ header: t('familyMemberReference'), getUrl, getReference }}
                renderActions={c => isEditUser ? <Actions {...c} /> : null}
            />
        </div>
    )
}

export function TotalCostsFooter({costs, hasReferenceColumn}: {costs: CostItem[], hasReferenceColumn?: boolean}) {
    const {t} = useTranslation()

    const {team} = useSingleTeamMangement()
    const {fxConverter} = useFxContext()

    if (costs.length === 0) return null

    const ccy = team?.currency ?? 'EUR'

    const total = _(costs).map(c => fxConverter.convert(c.amount, c.currency, ccy)).sum()
    const vat = _(costs).map(c => fxConverter.convert(c.vat ?? 0.0, c.currency, ccy)).sum()

    return (
        <tfoot>
            <tr className="pc-footer-row">
                <td className="pc-cell whitespace-nowrap text-right text-pcx-600 font-semibold uppercase tracking-wider">{t('total')}</td>
                {hasReferenceColumn && <td className="pc-cell" />}
                <td className="pc-cell whitespace-nowrap text-right">{format.format(total)} {ccy}</td>
                <td className="pc-cell whitespace-nowrap text-right">{format.format(vat)} {ccy}</td>
            </tr>
        </tfoot>
    )
}

export function CostItemDetails({cost, format}: {cost: CostItem, format: Intl.NumberFormat}) {
    const {t} = useTranslation()
    return <>
        <div className="grid grid-cols-[auto_auto] gap-y-1 items-end border-t border-b border-pcx-200 pb-1">
            <div className="label">{t('amount')}:</div>
            <div className="justify-self-end"><span className="text-2xl font-light">{format.format(cost.amount)}</span> {cost.currency}</div>

            <div className="label">{t('vat')}:</div>
            <div className="justify-self-end">{format.format(cost.vat)} {cost.currency}</div>
        </div>
        <div className="text-slate-600 pt-2 flex flex-row justify-between gap-2">
            <div>{cost.reference} @ {cost.date}</div>
            {cost.costCenter && <div className="ribbon text-sm">{cost.costCenter}</div>}
        </div>
        <div className="text-slate-400 text-sm max-w-xs empty:hidden pt-2">
            {cost.comment}
        </div>
    </>
}